"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Notice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  ComparisonTable,
  fitMessage,
  MeansPanel,
  PopulationSketch,
} from "@/components/tools/stats-distributions-more/clt-parts";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  momentsOf,
  POPULATION_IDS,
  POPULATIONS,
  type PopulationId,
  simulateMeans,
} from "@/lib/tool-kit/stats-distributions-more/clt";

/** English strings. A translated content file overrides these through `ui`. */
export const CENTRAL_LIMIT_THEOREM_SIMULATOR_STRINGS = {
  populationLabel: "Population to sample from",
  popNormal: "Normal — bell-shaped, mean 0, SD 1",
  popUniform: "Uniform — flat between 0 and 1",
  popSkewed: "Right-skewed — exponential, mean 1",
  popVerySkewed: "Very right-skewed — lognormal, with a long tail",
  popBimodal: "Bimodal — two humps, around 2 and 8",
  popBinary: "Yes/no — 1 with probability 0.2, otherwise 0",
  nLabel: "Sample size n (draws averaged in each sample)",
  nHint: "From 1 to 1,000. Try 1, then 5, then 30, then 100.",
  quickSizes: "Quick sample sizes",
  countLabel: "Number of samples (one mean each)",
  countHint: "From 10 to 20,000. Samples × n can be at most 2,000,000 draws.",
  seedLabel: "Seed",
  seedHint: "The same seed always gives the same picture. Any text works.",
  newSeed: "New random seed",
  animate: "Animate the samples arriving",
  animating: "Showing {shown} of {total} samples",
  results: "Results",
  runSummary:
    "{samples}, each the mean of {n} draws from this population — {draws} in all. Seed: {seed}.",
  samplesWord: "one:{count} sample|other:{count} samples",
  drawsWord: "one:{count} random draw|other:{count} random draws",
  populationHeading: "The population",
  populationLine:
    "Population mean μ = {mean}, standard deviation σ = {sd}, skewness {skew}.",
  populationChartLabel:
    "Density curve of the population you are sampling from, with its mean marked",
  binaryChartLabel:
    "Bar chart of the yes/no population: 0 with probability 0.8 and 1 with probability 0.2",
  meansHeading: "The sample means",
  meansChartLabel:
    "Histogram of the simulated sample means with the normal curve the central limit theorem predicts drawn over it",
  meansCaption:
    "{shown} sample means (n = {n} each). The curve is the normal distribution the theorem predicts: mean {mean}, standard error {se}.",
  outsideNote:
    "one:{count} sample mean lies beyond four standard errors and is not drawn.|other:{count} sample means lie beyond four standard errors and are not drawn.",
  tableCaption: "What the theorem predicts against what this run produced",
  tableMeasure: "Measure",
  tableTheory: "Theory",
  tableSimulated: "This run",
  rowMean: "Mean of the sample means (μ)",
  rowSe: "SD of the sample means (σ/√n)",
  rowSkew: "Skewness of the sample means",
  rowWithin: "Means within ±1.96 SE (normal: 95%)",
  fitClose:
    "At n = {n} the predicted skewness of the sample means is {skew}, close enough to 0 that the normal curve fits well.",
  fitSkewed:
    "At n = {n} the predicted skewness of the sample means is {skew}: still visibly skewed, so the normal curve is only a rough fit. A larger n brings it closer.",
  honesty:
    "This is an illustration, not a proof. With a finite number of samples the bars wobble by a few percent around the curve, and a different seed shifts them again.",
  enterN: "Enter n as a whole number from 1 to 1,000.",
  enterCount:
    "Enter the number of samples as a whole number from 10 to 20,000.",
  enterBudget:
    "Samples × n must be at most 2,000,000 draws. Lower one of them.",
  enterSeed: "Enter a seed: any word or number.",
} as const;

type Strings = typeof CENTRAL_LIMIT_THEOREM_SIMULATOR_STRINGS;

const MAX_N = 1000;
const MIN_COUNT = 10;
const MAX_COUNT = 20_000;
const MAX_DRAWS = 2_000_000;
const QUICK_SIZES = [1, 2, 5, 10, 30, 100];
const FRAMES = 80;
const FRAME_MS = 40;

interface Config {
  population: PopulationId;
  n: number;
  count: number;
  seed: string;
}

function readConfig(
  population: PopulationId,
  nText: string,
  countText: string,
  seedText: string
): Config | keyof Strings {
  const n = parseIntegerInput(nText);
  if (n === null || n < 1 || n > MAX_N) {
    return "enterN";
  }
  const count = parseIntegerInput(countText);
  if (count === null || count < MIN_COUNT || count > MAX_COUNT) {
    return "enterCount";
  }
  if (n * count > MAX_DRAWS) {
    return "enterBudget";
  }
  const seed = seedText.trim();
  if (seed === "") {
    return "enterSeed";
  }
  return { population, n, count, seed };
}

/** Six random digits from the browser's secure generator — only ever on a click. */
function randomSeed(): string {
  const buffer = new Uint32Array(1);
  crypto.getRandomValues(buffer);
  return String(buffer[0] % 1_000_000).padStart(6, "0");
}

function Run({
  config,
  shownCount,
  strings,
}: {
  config: Config;
  /** How many of the samples to draw; null shows them all. */
  shownCount: number | null;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const population = POPULATIONS[config.population];
  const { n, count, seed } = config;
  const means = useMemo(
    () => simulateMeans(population, n, count, seed),
    [population, n, count, seed]
  );
  const shown = Math.min(shownCount ?? count, count);
  const moments = momentsOf(means, shown);
  const summary = t(strings.runSummary, {
    samples: tPlural(strings.samplesWord, locale, count, { count: fmt(count) }),
    n: fmt(n),
    draws: tPlural(strings.drawsWord, locale, count * n, {
      count: fmt(count * n),
    }),
    seed,
  });
  return (
    <>
      <p className="text-muted-foreground text-sm">{summary}</p>
      {shownCount === null ? null : (
        <p className="font-medium text-foreground text-sm">
          {t(strings.animating, { shown: fmt(shown), total: fmt(count) })}
        </p>
      )}
      <PopulationSketch fmt={fmt} labels={strings} population={population} />
      <MeansPanel
        fmt={fmt}
        labels={strings}
        locale={locale}
        means={means}
        n={n}
        population={population}
        shown={shown}
        total={count}
      />
      <ComparisonTable
        fmt={fmt}
        labels={strings}
        means={means}
        moments={moments}
        n={n}
        population={population}
      />
      <Notice>{fitMessage(population, n, strings, fmt)}</Notice>
      <p className="text-muted-foreground text-sm">{strings.honesty}</p>
    </>
  );
}

export default function CentralLimitTheoremSimulatorWidget() {
  const strings = useToolUi(CENTRAL_LIMIT_THEOREM_SIMULATOR_STRINGS);
  const fmt = useToolNumber();
  const [population, setPopulation] = useState<PopulationId>("skewed");
  const [nText, setNText] = useState("30");
  const [countText, setCountText] = useState("1000");
  const [seedText, setSeedText] = useState("2024");
  const [shownCount, setShownCount] = useState<number | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const stop = () => {
    if (timer.current !== null) {
      clearInterval(timer.current);
      timer.current = null;
    }
    setShownCount(null);
  };
  useEffect(
    () => () => {
      if (timer.current !== null) {
        clearInterval(timer.current);
      }
    },
    []
  );

  const config = readConfig(population, nText, countText, seedText);

  function animate(total: number) {
    stop();
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    const step = Math.max(1, Math.ceil(total / FRAMES));
    let current = 0;
    setShownCount(0);
    timer.current = setInterval(() => {
      current += step;
      if (current >= total) {
        stop();
      } else {
        setShownCount(current);
      }
    }, FRAME_MS);
  }

  const edit = (apply: () => void) => {
    stop();
    apply();
  };
  const names: Record<PopulationId, string> = {
    normal: strings.popNormal,
    uniform: strings.popUniform,
    skewed: strings.popSkewed,
    verySkewed: strings.popVerySkewed,
    bimodal: strings.popBimodal,
    binary: strings.popBinary,
  };

  return (
    <>
      <section className="space-y-5">
        <SelectField
          id="clt-population"
          label={strings.populationLabel}
          onChange={(value) => edit(() => setPopulation(value))}
          options={POPULATION_IDS.map((id) => ({
            value: id,
            label: names[id],
          }))}
          value={population}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <TextField
              hint={strings.nHint}
              id="clt-n"
              label={strings.nLabel}
              numeric
              onChange={(value) => edit(() => setNText(value))}
              value={nText}
            />
            <fieldset className="m-0 flex min-w-0 flex-wrap gap-2 border-0 p-0">
              <legend className="sr-only">{strings.quickSizes}</legend>
              {QUICK_SIZES.map((size) => (
                <button
                  className={BUTTON_SMALL}
                  key={size}
                  onClick={() => edit(() => setNText(String(size)))}
                  type="button"
                >
                  {fmt(size)}
                </button>
              ))}
            </fieldset>
          </div>
          <TextField
            hint={strings.countHint}
            id="clt-count"
            label={strings.countLabel}
            numeric
            onChange={(value) => edit(() => setCountText(value))}
            value={countText}
          />
          <TextField
            hint={strings.seedHint}
            id="clt-seed"
            label={strings.seedLabel}
            mono
            onChange={(value) => edit(() => setSeedText(value))}
            value={seedText}
          />
          <div className="flex flex-wrap items-end gap-3">
            <button
              className={BUTTON_SECONDARY}
              onClick={() => edit(() => setSeedText(randomSeed()))}
              type="button"
            >
              {strings.newSeed}
            </button>
            <button
              className={BUTTON_PRIMARY}
              disabled={typeof config === "string"}
              onClick={() =>
                typeof config === "object" && animate(config.count)
              }
              type="button"
            >
              {strings.animate}
            </button>
          </div>
        </div>
      </section>
      <section
        aria-live={shownCount === null ? "polite" : "off"}
        className="space-y-4"
      >
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {typeof config === "string" ? (
          <Notice>{strings[config]}</Notice>
        ) : (
          <Run config={config} shownCount={shownCount} strings={strings} />
        )}
      </section>
    </>
  );
}
